/**
 * 5 variants of the contact switcher for review.
 * Swap the export in Navbar.tsx to try each one:
 *   const ActiveContactSwitcher = VariantA; // (eller B, C, D, E)
 *
 * Variant A – Badges (desktop) + dropdown (mobil)
 * Variant B – Pill-tabs med fornavn
 * Variant C – Overlappende avatar-stack med popover
 * Variant D – Segmented control (iOS-stil)
 * Variant E – Kompakt chip: valgt kontakt + antall andre
 */

import { Fragment, useEffect, useRef, useState } from "react";
import { Representee } from "api/initial/useGetContactInfo";
import classNames from "classnames";
import { SelectedContact } from "providers/ContractIdProvider";
import { Tooltip } from "react-tooltip";
import { initials } from "utils/initials";

export const AVATAR_COLORS = [
  "#0A680F",
  "#3130ff",
  "#9F059F",
  "#003000",
  "#B60203",
  "#934100",
  "#5D5C00",
  "#00636B",
  "#660033",
  "#000066",
];

export interface ContactSwitcherProps {
  ownContact: SelectedContact;
  representees: Representee[];
  selectedContactId: string | number | undefined;
  onChange: (contact: SelectedContact) => void;
}

// ─────────────────────────────────────────────
// VARIANT A – Dropdown (mobil) + inline badges (desktop)
// ─────────────────────────────────────────────
const DropdownTrigger = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];
  const selected =
    allContacts.find((c) => String(c.id) === String(selectedContactId)) ?? ownContact;
  const selectedIndex = allContacts.findIndex((c) => String(c.id) === String(selectedContactId));
  const selectedColor = AVATAR_COLORS[Math.max(selectedIndex, 0) % AVATAR_COLORS.length];

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 hover:bg-capGreen/10 transition-colors focus:outline-none"
      >
        <span
          className="w-7 h-7 leading-none rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0"
          style={{ backgroundColor: selectedColor }}
        >
          {initials(selected.userName ?? "")}
        </span>
        <span className="hidden sm:block text-sm font-medium text-capGreen max-w-[10rem] truncate">
          {selected.userName}
        </span>
        <svg
          className={classNames("w-3 h-3 text-capGreen/60 transition-transform", {
            "rotate-180": open,
          })}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-capBeige border border-capGreen/20 rounded-lg shadow-lg z-[1100] py-1">
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-capGreen/50">
            Mine engasjement
          </p>
          {allContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            return (
              <button
                key={contact.id}
                onClick={() => {
                  setOpen(false);
                  onChange(contact);
                }}
                className={classNames(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-capGreen/10",
                  { "font-semibold": isSelected }
                )}
              >
                <span
                  className="w-6 h-6 rounded-full text-[10px] font-semibold text-white flex items-center justify-center shrink-0"
                  style={{ backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
                >
                  {initials(contact.userName ?? "")}
                </span>
                <span className="truncate text-capGreen">{contact.userName}</span>
                {isSelected && (
                  <svg
                    className="ml-auto w-3.5 h-3.5 text-capGreen shrink-0"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M2 7l4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InlineBadges = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];
  const selected = allContacts.find((c) => String(c.id) === String(selectedContactId));

  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-capGreen/50 leading-none">
        Mine engasjement
      </span>
      <div className="flex items-center gap-2 pt-0.5">
        <div className="flex items-center gap-1.5">
          {allContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            const tooltipId = `inline-a-${contact.id}`;
            return (
              <Fragment key={contact.id}>
                <button
                  id={tooltipId}
                  onClick={() => onChange(contact)}
                  style={{ backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
                  className={classNames(
                    "w-8 h-8 leading-none rounded-full text-xs font-semibold text-white flex items-center justify-center transition-all shrink-0",
                    {
                      "ring-2 ring-offset-1 ring-capGreen": isSelected,
                      "opacity-50 hover:opacity-80": !isSelected,
                    }
                  )}
                >
                  {initials(contact.userName ?? "")}
                </button>
                <Tooltip
                  anchorSelect={`#${tooltipId}`}
                  content={contact.userName}
                  place="bottom"
                  positionStrategy="fixed"
                />
              </Fragment>
            );
          })}
        </div>
        {selected?.userName && (
          <span className="text-sm font-medium text-capGreen truncate max-w-[10rem]">
            {selected.userName}
          </span>
        )}
      </div>
    </div>
  );
};

export const VariantA = (props: ContactSwitcherProps) => (
  <>
    <div className="lg:hidden">
      <DropdownTrigger {...props} />
    </div>
    <div className="hidden lg:block">
      <InlineBadges {...props} />
    </div>
  </>
);

// ─────────────────────────────────────────────
// VARIANT B – Pill tabs (firstname) + scrollable
// lg: firstname pill tabs   md: icon-only pills   sm: dropdown
// ─────────────────────────────────────────────
export const VariantB = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];

  const pills = (
    <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
      {allContacts.map((contact, index) => {
        const isSelected = String(contact.id) === String(selectedContactId);
        const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
        const firstname = contact.userName?.split(" ")[0] ?? "";
        return (
          <button
            key={contact.id}
            onClick={() => onChange(contact)}
            style={
              isSelected
                ? { backgroundColor: color, borderColor: color }
                : { borderColor: color, color }
            }
            className={classNames(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap",
              { "text-white": isSelected, "bg-transparent hover:opacity-80": !isSelected }
            )}
          >
            <span
              className="w-4 h-4 leading-none rounded-full text-[9px] font-bold text-white flex items-center justify-center shrink-0"
              style={{ backgroundColor: isSelected ? "rgba(255,255,255,0.3)" : color }}
            >
              {initials(contact.userName ?? "")}
            </span>
            {/* firstname on lg, initials-only on md */}
            <span className="hidden lg:inline">{firstname}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {/* sm: dropdown */}
      <div className="md:hidden">
        <DropdownTrigger
          ownContact={ownContact}
          representees={representees}
          selectedContactId={selectedContactId}
          onChange={onChange}
        />
      </div>
      {/* md+: pill tabs */}
      <div className="hidden md:flex items-center gap-1 overflow-x-auto no-scrollbar max-w-xs lg:max-w-sm">
        {pills}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// VARIANT C – Overlapping avatar stack, click opens popover
// All sizes: compact stacked avatars → popover list
// ─────────────────────────────────────────────
export const VariantC = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];

  const selected =
    allContacts.find((c) => String(c.id) === String(selectedContactId)) ?? ownContact;
  const selectedIndex = allContacts.findIndex((c) => String(c.id) === String(selectedContactId));

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  // Show max 4 avatars in the stack, rest as "+N"
  const MAX_STACK = 4;
  const stackContacts = allContacts.slice(0, MAX_STACK);
  const overflow = allContacts.length - MAX_STACK;

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 group focus:outline-none"
      >
        {/* Stacked avatars */}
        <div className="flex items-center">
          {stackContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            return (
              <span
                key={contact.id}
                style={{
                  backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
                  marginLeft: index === 0 ? 0 : "-0.5rem",
                  zIndex: isSelected ? 10 : stackContacts.length - index,
                }}
                className={classNames(
                  "relative w-7 h-7 rounded-full text-[10px] font-semibold text-white flex items-center justify-center border-2 border-capBeige transition-all",
                  { "ring-2 ring-capGreen": isSelected }
                )}
              >
                {initials(contact.userName ?? "")}
              </span>
            );
          })}
          {overflow > 0 && (
            <span
              style={{ marginLeft: "-0.5rem", zIndex: 0 }}
              className="relative w-7 h-7 rounded-full bg-capGreen/20 text-[10px] font-semibold text-capGreen flex items-center justify-center border-2 border-capBeige"
            >
              +{overflow}
            </span>
          )}
        </div>
        {/* Selected name (lg only) */}
        <span className="hidden lg:block text-sm font-medium text-capGreen truncate max-w-[8rem]">
          {selected.userName}
        </span>
        <svg
          className={classNames(
            "w-3 h-3 text-capGreen/50 transition-transform group-hover:text-capGreen",
            { "rotate-180": open }
          )}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-60 bg-capBeige border border-capGreen/20 rounded-xl shadow-xl z-[1100] py-2 overflow-hidden">
          <p className="px-4 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-widest text-capGreen/40">
            Mine engasjement
          </p>
          {allContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
            return (
              <button
                key={contact.id}
                onClick={() => {
                  setOpen(false);
                  onChange(contact);
                }}
                className={classNames(
                  "w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-capGreen/10",
                  { "bg-capGreen/5": isSelected }
                )}
              >
                <span
                  className="w-8 h-8 rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0 shadow-sm"
                  style={{ backgroundColor: color }}
                >
                  {initials(contact.userName ?? "")}
                </span>
                <div className="flex flex-col min-w-0">
                  <span
                    className={classNames("truncate text-capGreen", {
                      "font-semibold": isSelected,
                    })}
                  >
                    {contact.userName}
                  </span>
                  {isSelected && <span className="text-[10px] text-capGreen/50">Valgt</span>}
                </div>
                {isSelected && (
                  <svg
                    className="ml-auto w-4 h-4 text-capGreen shrink-0"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M2 7l4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// VARIANT D – Segmented control (iOS-stil)
// sm: dropdown   md+: segmented control med fornavn
// Valgt segment har solid bakgrunn i kontaktfargen, resten er transparente
// ─────────────────────────────────────────────
export const VariantD = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];
  const selectedIndex = allContacts.findIndex((c) => String(c.id) === String(selectedContactId));
  const selectedColor = AVATAR_COLORS[Math.max(selectedIndex, 0) % AVATAR_COLORS.length];

  return (
    <>
      {/* sm: dropdown */}
      <div className="md:hidden">
        <DropdownTrigger
          ownContact={ownContact}
          representees={representees}
          selectedContactId={selectedContactId}
          onChange={onChange}
        />
      </div>
      {/* md+: segmented control */}
      <div
        className="hidden md:flex items-stretch rounded-full border border-capGreen/25 overflow-hidden bg-capGreen/5"
        style={{ height: "2rem" }}
      >
        {allContacts.map((contact, index) => {
          const isSelected = String(contact.id) === String(selectedContactId);
          const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
          const firstname = contact.userName?.split(" ")[0] ?? "";
          return (
            <button
              key={contact.id}
              onClick={() => onChange(contact)}
              style={isSelected ? { backgroundColor: color } : {}}
              className={classNames(
                "flex items-center gap-1.5 px-3 text-xs font-medium transition-all whitespace-nowrap border-r border-capGreen/15 last:border-r-0",
                {
                  "text-white": isSelected,
                  "text-capGreen hover:bg-capGreen/10": !isSelected,
                }
              )}
            >
              <span
                className="w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 transition-all"
                style={{
                  backgroundColor: isSelected ? "rgba(255,255,255,0.25)" : color,
                  color: "white",
                }}
              >
                {initials(contact.userName ?? "")}
              </span>
              <span className="hidden lg:inline">{firstname}</span>
            </button>
          );
        })}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// VARIANT E – Kompakt chip: vis kun valgt kontakt + antall andre
// Klikk åpner en liste. Ryddig på alle størrelser.
// ─────────────────────────────────────────────
export const VariantE = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];
  const selectedIndex = allContacts.findIndex((c) => String(c.id) === String(selectedContactId));
  const selected = allContacts[selectedIndex] ?? ownContact;
  const selectedColor = AVATAR_COLORS[Math.max(selectedIndex, 0) % AVATAR_COLORS.length];
  const othersCount = allContacts.length - 1;

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-8 rounded-full border border-capGreen/25 bg-capGreen/5 pl-1 pr-2.5 hover:bg-capGreen/10 transition-colors focus:outline-none"
      >
        {/* Selected avatar */}
        <span
          className="w-6 h-6 rounded-full text-[10px] font-semibold text-white flex items-center justify-center shrink-0"
          style={{ backgroundColor: selectedColor }}
        >
          {initials(selected.userName ?? "")}
        </span>
        {/* Selected name – hidden on very small screens */}
        <span className="hidden sm:block text-xs font-medium text-capGreen truncate max-w-[8rem]">
          {selected.userName?.split(" ")[0]}
        </span>
        {/* Others count badge */}
        {othersCount > 0 && (
          <span className="text-[10px] font-semibold text-capGreen/50">+{othersCount}</span>
        )}
        <svg
          className={classNames("w-3 h-3 text-capGreen/50 transition-transform", {
            "rotate-180": open,
          })}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-capBeige border border-capGreen/20 rounded-xl shadow-xl z-[1100] py-2 overflow-hidden">
          <p className="px-4 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-widest text-capGreen/40">
            Mine engasjement
          </p>
          {allContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
            return (
              <button
                key={contact.id}
                onClick={() => {
                  setOpen(false);
                  onChange(contact);
                }}
                className={classNames(
                  "w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors hover:bg-capGreen/10",
                  { "bg-capGreen/5 font-semibold": isSelected }
                )}
              >
                <span
                  className="w-7 h-7 rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {initials(contact.userName ?? "")}
                </span>
                <span className="truncate text-capGreen">{contact.userName}</span>
                {isSelected && (
                  <svg
                    className="ml-auto w-3.5 h-3.5 text-capGreen shrink-0"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M2 7l4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
